import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CURRENCY_DEFS,
  LEDGER_TYPE_DEFS,
  LEDGER_TYPE_MAP,
  type LedgerAccountType,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { money } from '../../lib/format';
import { useListState } from '../../lib/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, PageHead, SearchBox } from '../../components/ui';
import { LedgerHistory } from '../../components/LedgerHistory';
import { ExportMenu } from '../../components/ExportMenu';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  matchesSearch,
  useLocalFilterTable,
  type FilterDef,
  type LocalAccessors,
} from '../../components/ColumnFilter';
import { exportDay, exportNotes, type TableExportDoc } from '../../lib/tableExport';
import { fxMoney, isForeignCurrency } from '../../lib/currency';

interface AccountNode {
  id: string;
  code: string;
  name: string;
  type: LedgerAccountType;
  parentId: string | null;
  systemKey: string | null;
  isGroup: boolean;
  isActive: boolean;
  level: number;
  debit: number;
  credit: number;
  balance: number;
  /** عملة الحساب، والرصيد بها إن كانت أجنبية. */
  currency?: string;
  fxBalance?: number | null;
  children: AccountNode[];
}

interface ChartData {
  accounts: AccountNode[];
  totals: Record<LedgerAccountType, number>;
}

/** أعمدة الفلتر — محلّية: الدليل يصل كاملًا بأرصدته في طلب واحد. */
const CHART_FILTERS: FilterDef[] = [
  { key: 'code', label: 'الكود', kind: 'text', sortable: true },
  { key: 'name', label: 'اسم الحساب', kind: 'text', sortable: true },
  {
    key: 'type',
    label: 'النوع',
    kind: 'enum',
    options: LEDGER_TYPE_DEFS.map((t) => ({ value: t.key, label: t.label })),
  },
  {
    key: 'nature',
    label: 'الصنف',
    kind: 'enum',
    options: [
      { value: 'group', label: 'تجميعي' },
      { value: 'leaf', label: 'فرعي' },
    ],
  },
  {
    key: 'state',
    label: 'الحالة',
    kind: 'enum',
    options: [
      { value: 'active', label: 'نشط' },
      { value: 'inactive', label: 'معطّل' },
    ],
  },
  { key: 'debit', label: 'مدين', kind: 'number', sortable: true },
  { key: 'credit', label: 'دائن', kind: 'number', sortable: true },
  { key: 'balance', label: 'الرصيد', kind: 'number', sortable: true },
];

const CHART_ACCESSORS: LocalAccessors<AccountNode> = {
  nature: (n) => (n.isGroup ? 'group' : 'leaf'),
  state: (n) => (n.isActive ? 'active' : 'inactive'),
};

/** صفٌّ معروض: الحساب، وهل جاء سياقًا لا مطابقةً (أبٌ لحسابٍ مطابق). */
interface ShownRow {
  node: AccountNode;
  context: boolean;
  level: number;
}

export function ChartOfAccounts() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [opened, setOpened] = useState<AccountNode | null>(null);
  const { state, patch } = useListState();
  const [search, setSearch] = useState(state.q ?? '');

  const chart = useQuery({
    queryKey: ['chart'],
    queryFn: () => api<ChartData>('/accounting/accounts'),
  });

  const provision = useMutation({
    mutationFn: () => api<{ created: number }>('/accounting/accounts/provision', { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['chart'] }),
  });

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /* الشجرة مسطّحةً بترتيبها — كل الحسابات، مطويّها وظاهرها. */
  const flat = useMemo(() => {
    const out: AccountNode[] = [];
    const walk = (nodes: AccountNode[]): void => {
      for (const node of nodes) {
        out.push(node);
        walk(node.children);
      }
    };
    walk(chart.data?.accounts ?? []);
    return out;
  }, [chart.data]);

  const table = useLocalFilterTable({
    rows: flat,
    defs: CHART_FILTERS,
    accessors: CHART_ACCESSORS,
    state,
    patch,
    search: search ? (n: AccountNode) => matchesSearch(search, n.code, n.name) : undefined,
  });

  const searching = Boolean(search.trim()) || table.active > 0;
  const sorting = Boolean(table.sort);

  /*
   * ثلاث حالات للعرض:
   * - بلا بحث ولا فلتر ولا فرز: الشجرة كما هي، بطيّها وتوسيعها.
   * - مع بحث أو فلتر: الحسابات المطابقة في مواضعها من الشجرة ومعها آباؤها
   *   باهتين — «النقدية بالخزينة» بلا «الأصول المتداولة» فوقها لا يُعرف
   *   مكانه. والطيّ معطّل هنا: المطابق لا يُخفى.
   * - مع فرز: قائمة مسطّحة بلا إزاحة — ترتيب الأرصدة يكسر الشجرة أصلًا.
   */
  const rows = useMemo<ShownRow[]>(() => {
    if (sorting) return table.rows.map((node) => ({ node, context: false, level: 0 }));
    if (searching) {
      const matched = new Set(table.rows.map((n) => n.id));
      const byId = new Map(flat.map((n) => [n.id, n]));
      const keep = new Set<string>();
      for (const id of matched) {
        let cur = byId.get(id);
        while (cur && !keep.has(cur.id)) {
          keep.add(cur.id);
          cur = cur.parentId ? byId.get(cur.parentId) : undefined;
        }
      }
      return flat
        .filter((n) => keep.has(n.id))
        .map((node) => ({ node, context: !matched.has(node.id), level: node.level }));
    }
    const out: ShownRow[] = [];
    const walk = (nodes: AccountNode[]): void => {
      for (const node of nodes) {
        out.push({ node, context: false, level: node.level });
        if (!collapsed.has(node.id)) walk(node.children);
      }
    };
    walk(chart.data?.accounts ?? []);
    return out;
  }, [sorting, searching, table.rows, flat, collapsed, chart.data]);

  if (chart.isError) {
    return (
      <div className="page">
        <ErrorState error={chart.error} onRetry={() => void chart.refetch()} />
      </div>
    );
  }

  const empty = chart.data?.accounts.length === 0;
  const matchedCount = rows.filter((r) => !r.context).length;

  const buildExport = (): TableExportDoc => ({
    title: 'دليل الحسابات',
    sheets: [
      {
        title: 'دليل الحسابات',
        period: { label: `كما في ${exportDay(new Date().toISOString())}` },
        sections: [
          {
            columns: [
              { key: 'code', label: 'الكود' },
              { key: 'name', label: 'اسم الحساب', indent: true },
              { key: 'type', label: 'النوع' },
              { key: 'nature', label: 'الصنف' },
              { key: 'state', label: 'الحالة' },
              { key: 'debit', label: 'مدين', kind: 'money' },
              { key: 'credit', label: 'دائن', kind: 'money' },
              { key: 'balance', label: 'الرصيد', kind: 'money' },
            ],
            rows: rows.map(({ node, level }) => ({
              _level: Math.min(level, 8),
              _style: node.isGroup ? 'group' : undefined,
              code: node.code,
              name: node.name,
              type: LEDGER_TYPE_MAP[node.type]?.label ?? node.type,
              nature: node.isGroup ? 'تجميعي' : 'فرعي',
              state: node.isActive ? 'نشط' : 'معطّل',
              debit: node.debit,
              credit: node.credit,
              balance: node.balance,
            })),
            /* أرصدة التجميعيّ تشمل فروعه، فجمع العمود يكرّرها. */
            totals: null,
          },
        ],
        notes: exportNotes({
          q: search,
          conditions: describeFilters(CHART_FILTERS, table.filters),
          count: matchedCount,
          total: flat.length,
          truncated: false,
          unit: 'الحسابات',
        }),
      },
    ],
  });

  return (
    <div className="page">
      <PageHead
        title="دليل الحسابات"
        subtitle="شجرة الحسابات وأرصدتها من القيود المرحَّلة"
        actions={
          atLeast('ADMIN') &&
          !empty && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={15} />
              حساب جديد
            </button>
          )
        }
      />

      {empty ? (
        <div className="card">
          <EmptyState
            title="لم تُهيَّأ المحاسبة بعد"
            text="سيُنشأ دليل حسابات مبدئي مناسب لشركة خدمية، ويمكنك تعديله وإعادة ترقيمه كما تشاء بعد ذلك."
            action={
              atLeast('ADMIN') && (
                <button
                  className="btn btn-primary"
                  onClick={() => provision.mutate()}
                  disabled={provision.isPending}
                >
                  {provision.isPending ? 'جارٍ التهيئة' : 'تهيئة دليل الحسابات'}
                </button>
              )
            }
          />
        </div>
      ) : (
        <>
          <div className="stats">
            {LEDGER_TYPE_DEFS.map((t) => (
              <div className="stat" key={t.key}>
                <div className="stat-label">{t.label}</div>
                <div className="stat-value num">{money(chart.data?.totals[t.key] ?? 0)}</div>
              </div>
            ))}
          </div>

          <div className="toolbar sticky-bar">
            <SearchBox
              value={search}
              onChange={(v) => {
                setSearch(v);
                patch({ q: v || undefined });
              }}
              placeholder="بحث بالكود أو اسم الحساب"
            />
            {searching && !sorting && (
              <span className="small mute">
                {matchedCount} مطابقًا — الآباء الباهتة سياقٌ للموضع
              </span>
            )}
            <ExportMenu build={buildExport} disabled={!rows.length} />
            <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
              {sorting && (
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => patch({ sort: undefined, order: undefined })}
                  title="إلغاء الفرز والعودة إلى الشجرة"
                >
                  عرض الشجرة
                </button>
              )}
              <FilterButton {...table.button} />
            </span>
          </div>

          <FilterChips {...table.chips} />

          <FilterScope {...table.scope}>
          <div className="card">
            <div className="table-wrap sticky-head">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>
                      الكود
                      <HeaderFilter colKey="code" />
                    </th>
                    <th>
                      اسم الحساب
                      <HeaderFilter colKey="name" />
                    </th>
                    <th style={{ width: 110 }}>
                      النوع
                      <HeaderFilter colKey="type" />
                    </th>
                    <th style={{ width: 90 }}>
                      الصنف
                      <HeaderFilter colKey="nature" />
                    </th>
                    <th style={{ width: 90 }}>
                      الحالة
                      <HeaderFilter colKey="state" />
                    </th>
                    <th style={{ width: 130 }}>
                      مدين
                      <HeaderFilter colKey="debit" />
                    </th>
                    <th style={{ width: 130 }}>
                      دائن
                      <HeaderFilter colKey="credit" />
                    </th>
                    <th style={{ width: 140 }}>
                      الرصيد
                      <HeaderFilter colKey="balance" />
                    </th>
                    <th style={{ width: 48 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({ node, context, level }) => {
                    /* الطيّ في الشجرة الكاملة وحدها: في البحث والفرز يُعرض المطابق كلّه. */
                    const canFold = !searching && !sorting && node.children.length > 0;
                    const isCollapsed = collapsed.has(node.id);
                    return (
                      <tr
                        key={node.id}
                        className={node.isGroup ? 'row-group' : undefined}
                        style={context ? { opacity: 0.55 } : undefined}
                      >
                        <td className="num cell-mute">{node.code}</td>
                        <td>
                          <span
                            className="row"
                            style={{ paddingInlineStart: level * 18 }}
                          >
                            {canFold ? (
                              <button
                                className="btn btn-icon btn-ghost btn-sm"
                                onClick={() => toggle(node.id)}
                                aria-label={isCollapsed ? 'توسيع' : 'طي'}
                                aria-expanded={!isCollapsed}
                              >
                                <Icon name={isCollapsed ? 'chevronLeft' : 'chevronDown'} size={14} />
                              </button>
                            ) : (
                              <span style={{ width: 26 }} />
                            )}
                            <span className={node.isGroup ? 'strong' : undefined}>{node.name}</span>
                            {node.systemKey && (
                              <span
                                className="app-soon-tag"
                                title="حساب نظامي يستخدمه الترحيل التلقائي"
                              >
                                نظامي
                              </span>
                            )}
                          </span>
                        </td>
                        <td>
                          <Badge kind={LEDGER_TYPE_MAP[node.type]?.kind}>
                            {LEDGER_TYPE_MAP[node.type]?.label}
                          </Badge>
                        </td>
                        <td className="cell-mute">{node.isGroup ? 'تجميعي' : 'فرعي'}</td>
                        <td>
                          {node.isActive ? (
                            <span className="cell-mute">نشط</span>
                          ) : (
                            <Badge kind="neutral">معطّل</Badge>
                          )}
                        </td>
                        <td className="num cell-mute">{node.debit ? money(node.debit) : '—'}</td>
                        <td className="num cell-mute">{node.credit ? money(node.credit) : '—'}</td>
                        <td className="num strong">
                          {node.isGroup || node.debit || node.credit ? (
                            <Link to={`/finance/accounts/${node.id}`}>{money(node.balance)}</Link>
                          ) : (
                            '—'
                          )}
                          {node.fxBalance != null && (
                            <div className="tiny mute">{fxMoney(node.fxBalance, node.currency)}</div>
                          )}
                        </td>
                        <td className="end">
                          <button
                            className="btn btn-icon btn-ghost btn-sm"
                            onClick={() => setOpened(node)}
                            title={atLeast('ADMIN') ? 'تعديل الحساب وسجل تعديلاته' : 'سجل تعديلات الحساب'}
                            aria-label="تعديل الحساب وسجل تعديلاته"
                          >
                            <Icon name={atLeast('ADMIN') ? 'edit' : 'clock'} size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {chart.data && rows.length === 0 && (
              <EmptyState title="لا حسابات مطابقة" text="لا حساب يطابق هذا البحث أو الفلتر." />
            )}
          </div>
          </FilterScope>
        </>
      )}

      {creating && (
        <CreateAccountModal
          /* كل الحسابات التجميعية لا الظاهر منها وحده — الطيّ والبحث لا يُخفيان أبًا ممكنًا. */
          accounts={flat.filter((r) => r.isGroup)}
          onClose={() => setCreating(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['chart'] });
            setCreating(false);
          }}
        />
      )}

      {opened && (
        <AccountModal
          account={opened}
          tree={chart.data?.accounts ?? []}
          canEdit={atLeast('ADMIN')}
          onClose={() => setOpened(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['chart'] });
            void queryClient.invalidateQueries({ queryKey: ['account-history', opened.id] });
            void queryClient.invalidateQueries({ queryKey: ['accounts', 'postable'] });
            setOpened(null);
          }}
        />
      )}
    </div>
  );
}

function CreateAccountModal({
  accounts,
  onClose,
  onSaved,
}: {
  accounts: AccountNode[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [isGroup, setIsGroup] = useState(false);
  const [currency, setCurrency] = useState('EGP');

  const parent = accounts.find((a) => a.id === parentId);

  const create = useMutation({
    mutationFn: () =>
      api('/accounting/accounts', {
        method: 'POST',
        body: {
          code: code.trim(),
          name: name.trim(),
          // النوع يتبع الأب دائمًا: حساب فرعي بنوع مختلف يفسد التجميع.
          type: parent?.type ?? 'ASSET',
          parentId: parentId || undefined,
          isGroup,
          // التجميعي بالجنيه دائمًا: يجمع أبناءً قد تختلف عملاتهم.
          currency: isGroup ? 'EGP' : currency,
        },
      }),
    onSuccess: onSaved,
  });

  const valid = code.trim() && name.trim().length >= 2 && parentId;

  return (
    <Modal
      title="حساب جديد"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => create.mutate()}
            disabled={!valid || create.isPending}
          >
            {create.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error ? create.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="col">
        <div className="field">
          <label className="label req" htmlFor="a-parent">
            الحساب الأب
          </label>
          <select
            id="a-parent"
            className="select"
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">اختر حسابًا تجميعيًا</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {'—'.repeat(a.level)} {a.code} — {a.name}
              </option>
            ))}
          </select>
          {parent && (
            <span className="tiny mute">
              سيكون الحساب من نوع {LEDGER_TYPE_MAP[parent.type].label}، تبعًا للأب.
            </span>
          )}
        </div>

        <div className="form-grid">
          <div className="field">
            <label className="label req" htmlFor="a-code">
              الكود
            </label>
            <input
              id="a-code"
              className="input num"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="5295"
              dir="ltr"
            />
          </div>

          <div className="field">
            <label className="label req" htmlFor="a-name">
              اسم الحساب
            </label>
            <input
              id="a-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>

        <label className="row" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={isGroup}
            onChange={(e) => setIsGroup(e.target.checked)}
          />
          <span className="small">حساب تجميعي — يجمع أرصدة أبنائه ولا يقبل قيودًا مباشرة</span>
        </label>

        {!isGroup && (
          <CurrencyField id="a-currency" value={currency} onChange={setCurrency} />
        )}
      </div>
    </Modal>
  );
}

/**
 * تعديل حساب وسجل تعديلاته.
 *
 * التعديل لمدير النظام كما في الخادم، والسجل لكل من يرى الدليل: من غيّر
 * اسم الحساب أو نقله تحت أب آخر سؤالٌ يطرحه المحاسب لا المدير وحده.
 */
function AccountModal({
  account,
  tree,
  canEdit,
  onClose,
  onSaved,
}: {
  account: AccountNode;
  tree: AccountNode[];
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(account.code);
  const [name, setName] = useState(account.name);
  const [parentId, setParentId] = useState(account.parentId ?? '');
  const [isActive, setIsActive] = useState(account.isActive);
  const [currency, setCurrency] = useState(account.currency ?? 'EGP');

  /*
   * الآباء الممكنون: تجميعيون من النوع نفسه، لا الحساب نفسه ولا فروعه —
   * من الشجرة كاملة لا من الصفوف المعروضة، فالمطويّ يبقى خيارًا.
   */
  const descendants = new Set<string>();
  const collect = (n: AccountNode) => {
    descendants.add(n.id);
    n.children.forEach(collect);
  };
  const parents: AccountNode[] = [];
  const walk = (nodes: AccountNode[]) => {
    for (const n of nodes) {
      if (n.id === account.id) collect(n);
      else if (n.isGroup && n.type === account.type && !descendants.has(n.id)) parents.push(n);
      walk(n.children);
    }
  };
  walk(tree);
  const options = parents.filter((p) => !descendants.has(p.id));

  const save = useMutation({
    mutationFn: () =>
      api(`/accounting/accounts/${account.id}`, {
        method: 'PATCH',
        body: {
          code: code.trim(),
          name: name.trim(),
          parentId: parentId || null,
          isActive,
          ...(currency !== (account.currency ?? 'EGP') ? { currency } : {}),
        },
      }),
    onSuccess: onSaved,
  });

  const valid = code.trim() && name.trim().length >= 2;

  return (
    <Modal
      title={`الحساب ${account.code} — ${account.name}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            {canEdit ? 'إلغاء' : 'إغلاق'}
          </button>
          {canEdit && (
            <button
              className="btn btn-primary"
              onClick={() => save.mutate()}
              disabled={!valid || save.isPending}
            >
              {save.isPending ? 'جارٍ الحفظ' : 'حفظ التعديل'}
            </button>
          )}
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {canEdit && (
        <div className="col mb-4">
          <div className="form-grid">
            <div className="field">
              <label className="label req" htmlFor="e-code">
                الكود
              </label>
              <input
                id="e-code"
                className="input num"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                dir="ltr"
              />
            </div>
            <div className="field">
              <label className="label req" htmlFor="e-name">
                اسم الحساب
              </label>
              <input
                id="e-name"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>

          <div className="field">
            <label className="label" htmlFor="e-parent">
              الحساب الأب
            </label>
            <select
              id="e-parent"
              className="select"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              {!account.parentId && <option value="">بلا أب — حساب رئيسي</option>}
              {options.map((a) => (
                <option key={a.id} value={a.id}>
                  {'—'.repeat(a.level)} {a.code} — {a.name}
                </option>
              ))}
            </select>
          </div>

          <label className="row" style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isActive}
              disabled={!!account.systemKey && account.isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span className="small">
              مفعّل
              {account.systemKey && ' — حساب نظامي لا يُعطَّل، يعتمد عليه الترحيل التلقائي'}
            </span>
          </label>

          {!account.isGroup && (
            <CurrencyField
              id="e-currency"
              value={currency}
              onChange={setCurrency}
              locked={!!(account.debit || account.credit)}
            />
          )}
        </div>
      )}

      <h3 className="card-title mb-4">سجل التعديلات</h3>
      <LedgerHistory
        path={`/accounting/accounts/${account.id}/history`}
        queryKey={['account-history', account.id]}
      />
    </Modal>
  );
}

/** عملة الحساب الفرعي — تُقفل بعد أول قيد عليه (والخادم يفرض ذلك أيضًا). */
function CurrencyField({
  id,
  value,
  onChange,
  locked = false,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  locked?: boolean;
}) {
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        العملة
      </label>
      <select
        id={id}
        className="select"
        value={value}
        disabled={locked}
        onChange={(e) => onChange(e.target.value)}
      >
        {CURRENCY_DEFS.map((c) => (
          <option key={c.code} value={c.code}>
            {c.label} ({c.symbol})
          </option>
        ))}
      </select>
      <span className="tiny mute">
        {locked
          ? 'عليه قيود، فلا تتغيّر عملته. افتح حسابًا جديدًا بالعملة المطلوبة وانقل الرصيد بقيد.'
          : isForeignCurrency(value)
            ? 'القيود عليه تحمل المبلغ بهذه العملة وسعرها، والدفاتر بالجنيه. يُعاد تقييم رصيده بسعر اليوم في القوائم.'
            : 'الدفاتر بالجنيه.'}
      </span>
    </div>
  );
}
